import { createContext, useContext, useMemo, type ReactNode } from 'react';
import {
  useDemPageNotifications,
  type DemPageNotification,
  type PushDemPageNotificationInput,
} from '../hooks/useDemPageNotifications';
import {
  useDemDeliveryStatusNotifications,
  type DemPersistedNotification,
} from '../lib/demDeliveryNotifications';

type DemNotificationsContextValue = {
  items: DemPageNotification[];
  persisted: DemPersistedNotification[];
  unreadPersistedCount: number;
  activeToast: DemPageNotification | null;
  push: (input: PushDemPageNotificationInput) => void;
  dismissToast: () => void;
  clearAll: () => void;
  markPersistedRead: (id: string) => Promise<void>;
  markAllPersistedRead: () => Promise<void>;
};

const DemNotificationsContext = createContext<DemNotificationsContextValue | null>(null);

export function DemNotificationsProvider({ children }: { children: ReactNode }) {
  const page = useDemPageNotifications();
  const delivery = useDemDeliveryStatusNotifications(page.push);

  const value = useMemo<DemNotificationsContextValue>(
    () => ({
      items: page.items,
      persisted: delivery.persisted,
      unreadPersistedCount: delivery.persisted.filter((row) => !row.read_at).length,
      activeToast: page.activeToast,
      push: page.push,
      dismissToast: page.dismissToast,
      clearAll: () => {
        page.clearAll();
        void delivery.markAllRead().catch(() => {
          /* still clear session toasts even if DB mark-read fails */
        });
      },
      markPersistedRead: delivery.markRead,
      markAllPersistedRead: delivery.markAllRead,
    }),
    [page, delivery],
  );

  return <DemNotificationsContext.Provider value={value}>{children}</DemNotificationsContext.Provider>;
}

export function useDemNotifications(): DemNotificationsContextValue {
  const ctx = useContext(DemNotificationsContext);
  if (!ctx) {
    throw new Error('useDemNotifications must be used within DemNotificationsProvider.');
  }
  return ctx;
}
